import {
  Building2,
  CircleDollarSign,
  Database,
  Handshake,
  Mail,
  MapPin,
  MessageSquare,
  Percent,
  ShieldCheck,
  SlidersHorizontal,
  Star,
  Users,
  Workflow,
  type LucideIcon
} from 'lucide-react';
import { menuSections } from '@/config/menu-config';
import { getSectionPermissions } from '@/config/section-access';

export type CompanySettingsFrequency = 'Routine' | 'Careful' | 'Occasional' | 'Rare';

interface CompanySettingsCardPresentation {
  id: string;
  description: string;
  frequency: CompanySettingsFrequency;
  icon: LucideIcon;
  keywords: string;
}

interface CompanySettingsGroupPresentation {
  id: string;
  title: string;
  description: string;
  cards: readonly CompanySettingsCardPresentation[];
}

export interface CompanySettingsHubCard extends CompanySettingsCardPresentation {
  title: string;
}

export interface CompanySettingsHubGroup {
  id: string;
  title: string;
  description: string;
  cards: CompanySettingsHubCard[];
}

type HasAnyPermission = (permissions: string[]) => boolean;

const COMPANY_SETTINGS_PRESENTATION: readonly CompanySettingsGroupPresentation[] = [
  {
    id: 'identity',
    title: 'Identity & Branding',
    description: 'Who LendWise is, everywhere a borrower sees it.',
    cards: [
      {
        id: 'company-profile',
        description: 'Legal identity, EIN, HQ address, branding and disclaimers.',
        frequency: 'Occasional',
        icon: Building2,
        keywords: 'ein legal identity branding logo compliance disclaimers licensing nmls'
      },
      {
        id: 'branch-offices',
        description: 'Locations, branch managers and branch NMLS records.',
        frequency: 'Occasional',
        icon: MapPin,
        keywords: 'locations branches managers offices nmls'
      },
      {
        id: 'google-business-profile',
        description: 'Connect, verify and manage the company Google Business Profile.',
        frequency: 'Rare',
        icon: Star,
        keywords: 'google business profile oauth listing local search connection verification'
      }
    ]
  },
  {
    id: 'people',
    title: 'People & Access',
    description: 'Hiring, roles, and what each person can touch.',
    cards: [
      {
        id: 'manage-users',
        description: 'Approve, edit and deactivate; roles, licenses and integrations per person.',
        frequency: 'Routine',
        icon: Users,
        keywords: 'users people approve pending edit deactivate roles licenses integrations'
      },
      {
        id: 'role-permissions',
        description: 'Which sections each role can see and edit.',
        frequency: 'Careful',
        icon: ShieldCheck,
        keywords: 'permissions access gates roles security matrix'
      },
      {
        id: 'user-compensation',
        description: 'Comp plans, caps and change requests per loan officer.',
        frequency: 'Careful',
        icon: CircleDollarSign,
        keywords: 'comp compensation pay bps plans caps requests loan officer'
      }
    ]
  },
  {
    id: 'pricing',
    title: 'Pricing & Fees',
    description: 'What loans cost and who gets paid.',
    cards: [
      {
        id: 'closing-cost-admin',
        description: 'Default closing costs by state and product.',
        frequency: 'Careful',
        icon: Percent,
        keywords: 'closing cost costs fees state product defaults'
      },
      {
        id: 'title-escrow-companies',
        description: 'Settlement partners and their fee schedules.',
        frequency: 'Occasional',
        icon: Handshake,
        keywords: 'title escrow settlement companies partners fees schedules'
      },
      {
        id: 'condition-templates',
        description: 'Manage reusable conditions, automation rules and Clean Approval learning.',
        frequency: 'Occasional',
        icon: Workflow,
        keywords: 'condition conditions automation rules templates clean approval learning'
      },
      {
        id: 'ob-builder',
        description: 'Test Optimal Blue pricing scenarios and inspect request and result details.',
        frequency: 'Careful',
        icon: SlidersHorizontal,
        keywords: 'optimal blue ob builder pricing scenarios request result test'
      }
    ]
  },
  {
    id: 'communications',
    title: 'Communications',
    description: 'What LendWise sends, and when.',
    cards: [
      {
        id: 'messages',
        description: 'Review company email, SMS and call history with delivery and engagement status.',
        frequency: 'Routine',
        icon: MessageSquare,
        keywords: 'messages communications email sms calls delivery engagement history'
      },
      {
        id: 'email-notifications',
        description: 'Transactional email templates, recipients and accents.',
        frequency: 'Occasional',
        icon: Mail,
        keywords: 'email notifications templates recipients accents transactional'
      }
    ]
  },
  {
    id: 'integrations',
    title: 'Integrations & Data',
    description: 'Pipes in and out of Encompass.',
    cards: [
      {
        id: 'field-discovery',
        description: 'Explore Encompass fields and mappings.',
        frequency: 'Rare',
        icon: Database,
        keywords: 'encompass fields mappings discovery data integration'
      }
    ]
  }
] as const;

export const COMPANY_SETTINGS_PRESENTATION_IDS = COMPANY_SETTINGS_PRESENTATION.flatMap(
  (group) => group.cards.map((card) => card.id)
);

function hasPermission(
  permissions: string[] | undefined,
  hasAnyPermission: HasAnyPermission
): boolean {
  return !permissions || permissions.length === 0 || hasAnyPermission(permissions);
}

export function getCompanySettingsHubModel(
  hasAnyPermission: HasAnyPermission
): CompanySettingsHubGroup[] {
  const parent = menuSections
    .flatMap((section) => section.items)
    .find((item) => item.id === 'company-settings');
  if (!parent?.subItems) {
    throw new Error('Company Settings hub requires the company-settings menu children');
  }

  const sourceById = new Map(parent.subItems.map((item) => [item.id, item]));
  if (
    sourceById.size !== COMPANY_SETTINGS_PRESENTATION_IDS.length
    || COMPANY_SETTINGS_PRESENTATION_IDS.some((id) => !sourceById.has(id))
  ) {
    throw new Error('Company Settings hub presentation is out of sync with menu-config.ts');
  }

  return COMPANY_SETTINGS_PRESENTATION.flatMap((group) => {
    const cards = group.cards.flatMap((presentation) => {
      const source = sourceById.get(presentation.id)!;
      const permitted = hasPermission(source.permissions, hasAnyPermission)
        && hasPermission(getSectionPermissions(source.id), hasAnyPermission);
      return permitted ? [{ ...presentation, title: source.title }] : [];
    });
    return cards.length > 0 ? [{ ...group, cards }] : [];
  });
}
